import { CommonModule } from '@angular/common';
import {
  Component,
  Renderer2,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { AccessibilityBarComponent } from '../accessibility-bar/accessibility-bar.component';
import { Router, RouterLink, RouterModule } from '@angular/router';
import { IUser } from '../../portal/interfaces/global.interface';
import { GlobalService } from '../../portal/services/global.service';
import { Subscription } from 'rxjs';
import { animate, style, transition, trigger } from '@angular/animations';
import { AppointmentBookingComponent } from '../../website/components/appointment-booking/appointment-booking.component';
import { MatDialog } from '@angular/material/dialog';
import { LoginService } from '../../portal/pages/login/login.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, AccessibilityBarComponent, RouterModule, RouterLink],
  providers: [LoginService],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  animations: [
    trigger('dropdownAnimation', [
      // Animate dropdown in/out with fade and slide
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        animate(
          '200ms ease-out',
          style({ opacity: 1, transform: 'translateY(0)' }),
        ),
      ]),
      transition(':leave', [
        animate(
          '150ms ease-in',
          style({ opacity: 0, transform: 'translateY(-10px)' }),
        ),
      ]),
    ]),
  ],
})
export class HeaderComponent implements OnDestroy, OnInit {
  private isReading: boolean = false; // Tracks whether reading is active
  private utterance: SpeechSynthesisUtterance | null = null;
  private userSubscription: Subscription = new Subscription();
  user!: IUser | null;
  userDropdownVisible: boolean = false;
  isSearchActive = false;
  redirectButtonData: {
    text: string;
    redirectTo: string;
    queryParams?: any;
  } = {
    text: 'Go to Dashboard',
    redirectTo: '/dashboard',
  };
  private subcategoriesCache: any = null;
  constructor(
    private renderer: Renderer2,
    private router: Router,
    private globalService: GlobalService,
    private dialog: MatDialog,
    private loginService: LoginService,
  ) {
    this.setRedirectButtonData();
  }

  rediretectTo(link: string) {
    if (this.redirectButtonData.queryParams) {
      this.router.navigate([link], {
        queryParams: this.redirectButtonData.queryParams,
      });
    } else {
      this.router.navigate([link]);
    }
  }
  setRedirectButtonData() {
    const userStr = sessionStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    console.log(user, 'users');

    if (!user) return;

    if (user.role === 'admin') {
      this.redirectButtonData = {
        text: 'Go to Users Management',
        redirectTo: '/users-management',
      };
      return;
    }

    if (!user.isEmailVerified) {
      this.redirectButtonData = {
        text: 'Verify Email',
        redirectTo: '/required-details',
      };
      return;
    }

    if (!user.isCompanyDetailSubmitted) {
      this.redirectButtonData = {
        text: 'Submit Company Details',
        redirectTo: '/user-details',
      };
      return;
    }
    if (!user.isAuthorizedPersonDetailSubmitted) {
      this.redirectButtonData = {
        text: 'Submit Authrorized User Details',
        redirectTo: '/user-authorized-details',
      };
      return;
    }

    if (
      Array.isArray(user.accountsMainCategories) &&
      user.accountsMainCategories.length === 0 &&
      !user.isAssociate
    ) {
      this.redirectButtonData = {
        text: 'Go to Main Category',
        redirectTo: '/category-main',
      };
      return;
    }
    if (!user.isAssociate) {
      const accessToken = sessionStorage.getItem('accessToken');
      if (!accessToken) {
        this.redirectButtonData = {
          text: 'Go to Dashboard',
          redirectTo: '/dashboard',
        };
        return;
      }

      if (this.subcategoriesCache) {
        this.setSubcategoryRedirect(this.subcategoriesCache);
        return;
      }

      this.loginService.subcategoriesStep(accessToken).subscribe({
        next: (subcategoriesResult) => {
          this.subcategoriesCache = subcategoriesResult;
          this.setSubcategoryRedirect(subcategoriesResult);
        },
        error: (err) => {
          console.error('Error fetching subcategories', err);
          this.redirectButtonData = {
            text: 'Go to Dashboard',
            redirectTo: '/dashboard',
          };
        },
      });
      return;
    }
    if (user.isAdmin) {
      this.redirectButtonData = {
        text: 'Go to CMS',
        redirectTo: '/herosection',
      };
    }
    if (user.isAllFormSubmitted && !user.isAdmin) {
      this.redirectButtonData = {
        text: 'Go to Dashboard',
        redirectTo: '/dashboard',
      };
      return;
    }
  }

  logout() {
    sessionStorage.clear();
    localStorage.clear();
    this.user = null;
    window.location.reload();
  }
  private setSubcategoryRedirect(subcategoriesResult: any): void {
    if (subcategoriesResult?.status === 200 && subcategoriesResult.body?.[0]) {
      const categories = subcategoriesResult.body[0].selectedCategories;
      const mainCategoryId = subcategoriesResult.body[0].mainCategoryId;

      const firstUnsubmittedCategory = categories?.find((category: any) =>
        category.subcategories.some((sub: any) => !sub.isSubmitted),
      );

      if (firstUnsubmittedCategory) {
        const categoryRoute = this.getFormattedName(
          firstUnsubmittedCategory.categoryName,
        );

        this.redirectButtonData = {
          text: `Complete ${firstUnsubmittedCategory.categoryName} Form`,
          redirectTo: `/${categoryRoute}`,
          queryParams: { id: mainCategoryId },
        };
      } else {
        this.redirectButtonData = {
          text: 'Go to Dashboard',
          redirectTo: '/dashboard',
        };
      }
    } else {
      this.redirectButtonData = {
        text: 'Go to Dashboard',
        redirectTo: '/dashboard',
      };
    }
  }

  getShortUserName(): string {
    const threshold = 10;
    if (!this.user || !this.user.name) return '';
    return this.user.name.length > threshold
      ? this.user.name.slice(0, threshold) + '...'
      : this.user.name;
  }

  ngOnInit() {
    localStorage.removeItem('isEventPage');
    localStorage.removeItem('isAppointment');
    localStorage.removeItem('isIncentive');
    this.globalService.user$.subscribe((user) => {
      this.user = user;
    });
  }

  ngOnDestroy(): void {
    this.userSubscription.unsubscribe();
    if (this.utterance) {
      window.speechSynthesis.cancel();
    }
  }

  onRegisterClick(): void {
    localStorage.setItem('isIncentive', 'true');
    const userStr = sessionStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;

    if (!user) {
      this.router.navigate(['/required-details'], {
        queryParams: { applyForIncentive: true },
      });
      return;
    }

    if (!user.isEmailVerified) {
      this.router.navigate(['/required-details'], {
        queryParams: { applyForIncentive: true },
      });
      return;
    }

    if (!user.isCompanyDetailSubmitted) {
      this.router.navigate(['/user-details']);
      return;
    }

    if (!user.isAuthorizedPersonDetailSubmitted) {
      this.router.navigate(['/user-authorized-details']);
      return;
    }

    if (
      Array.isArray(user.accountsMainCategories) &&
      user.accountsMainCategories.length === 0 &&
      !user.isAssociate
    ) {
      this.router.navigate(['/category-main']);
      return;
    }
    if (!user.isAssociate) {
      const accessToken = sessionStorage.getItem('accessToken');
      if (!accessToken) {
        this.router.navigate(['/dashboard']);
        return;
      }

      if (this.subcategoriesCache) {
        this.navigateToSubcategoryOrDashboard(this.subcategoriesCache);
        return;
      }

      this.loginService.subcategoriesStep(accessToken).subscribe({
        next: (subcategoriesResult) => {
          this.subcategoriesCache = subcategoriesResult;
          this.navigateToSubcategoryOrDashboard(subcategoriesResult);
        },
        error: (err) => {
          console.error('Error fetching subcategories', err);
          this.router.navigate(['/dashboard']);
        },
      });

      return;
    }
    this.router.navigate(['/dashboard']);
  }

  private navigateToSubcategoryOrDashboard(subcategoriesResult: any): void {
    if (subcategoriesResult?.status === 200 && subcategoriesResult.body?.[0]) {
      const categories = subcategoriesResult.body[0].selectedCategories;
      const mainCategoryId = subcategoriesResult.body[0].mainCategoryId;

      const firstUnsubmittedCategory = categories?.find((category: any) =>
        category.subcategories.some((sub: any) => !sub.isSubmitted),
      );

      if (firstUnsubmittedCategory) {
        const categoryRoute = this.getFormattedName(
          firstUnsubmittedCategory.categoryName,
        );
        this.router.navigate([`/${categoryRoute}`], {
          queryParams: { id: mainCategoryId },
        });
      } else {
        this.router.navigate(['/dashboard']);
      }
    } else {
      this.router.navigate(['/dashboard']);
    }
  }

  // Close dropdown when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    const profileCard = target.closest('.profile-card');

    if (!profileCard && this.userDropdownVisible) {
      this.userDropdownVisible = false;
    }
  }

  toggleUserDropdown(event?: Event) {
    if (event) {
      event.stopPropagation(); // Prevent event bubbling
    }
    this.userDropdownVisible = !this.userDropdownVisible;
  }

  toggleSearch() {
    this.isSearchActive = !this.isSearchActive;
  }
  // Function to toggle screen reading
  toggleScreenReading(): void {
    if (this.isReading) {
      // Stop reading
      window.speechSynthesis.cancel();
      this.isReading = false;
      this.clearHighlights();
    } else {
      // Start reading
      const content = document.body.innerText; // Get all visible text
      this.utterance = new SpeechSynthesisUtterance(content);
      this.utterance.lang = 'en-US';
      this.utterance.rate = 1;
      this.utterance.pitch = 1;

      // Highlight text as it's being read
      this.utterance.onboundary = (event: SpeechSynthesisEvent) => {
        this.highlightWord(
          content,
          event.charIndex,
          event.charIndex + event.charLength,
        );
      };

      // Handle the end of speech
      this.utterance.onend = () => {
        this.isReading = false;
        this.clearHighlights();
      };

      window.speechSynthesis.speak(this.utterance);
      this.isReading = true;
    }
  }

  // Highlight the word currently being spoken
  private highlightWord(text: string, start: number, end: number): void {
    try {
      this.clearHighlights(); // Remove previous highlights

      // Create a range for the spoken text
      const range = document.createRange();
      const walker = document.createTreeWalker(
        document.body,
        NodeFilter.SHOW_TEXT,
        null,
      );

      let charCount = 0;
      let node: Node | null;

      while ((node = walker.nextNode())) {
        const length = node.textContent?.length || 0;
        if (charCount + length >= start) {
          const offsetStart = Math.max(0, start - charCount);
          const offsetEnd = Math.min(end - charCount, length);

          if (offsetStart < length && offsetEnd > 0) {
            range.setStart(node, offsetStart);
            range.setEnd(node, offsetEnd);

            const span = this.renderer.createElement('span');
            this.renderer.addClass(span, 'highlight');

            try {
              range.surroundContents(span);
            } catch (error) {
              // If we can't surround contents, just skip this highlight
              console.warn('Could not highlight text:', error);
            }
          }
          break;
        }
        charCount += length;
      }
    } catch (error) {
      console.warn('Error in highlightWord:', error);
    }
  }

  // Clear all highlights
  private clearHighlights(): void {
    try {
      const highlights = document.querySelectorAll('.highlight');
      highlights.forEach((highlight) => {
        const parent = highlight.parentNode;
        if (parent) {
          while (highlight.firstChild) {
            parent.insertBefore(highlight.firstChild, highlight);
          }
          parent.removeChild(highlight);
        }
      });

      // Normalize the text nodes to merge adjacent text nodes
      document.normalize();
    } catch (error) {
      console.warn('Error in clearHighlights:', error);
    }
  }

  onBookAppointment() {
    this.dialog
      .open(AppointmentBookingComponent, {
        width: '50%',
      })
      .afterClosed();
  }

  getFormattedName(name: string): string {
    return name.toLowerCase().replace(/\s+/g, '-');
  }
}
